<template>
  <div>
    <Navbar title="プレビュー"></Navbar>

    <div class="l-content">
      <div class="l-container">
        <div class="p-container">
          <div class="title">この内容で作成します</div>
        </div>
      </div>

      <div class="u-ptm u-pbm">
        <HorizontalLine></HorizontalLine>
      </div>

      <div class="l-container u-mtxl">
        <div class="p-container">
          <div class="preview">
            <div class="preview-label">基本情報</div>
            <div class="preview-body u-ptm">
              <div>{{ `${worker.last_name}  ${worker.first_name}` }}</div>
              <div class="u-pts">{{ `${worker.last_ruby} ${worker.first_ruby}` }}</div>
              <div class="u-pts" v-text="formattedGender">{{ formattedGender }}</div>
              <div class="u-pts" v-text="formatDate(worker.birth)"></div>
              <div class="u-pts" v-text="nationalityName"></div>
            </div>
          </div>


          <div class="preview">
            <div class="preview-label">郵便番号</div>
            <div class="preview-body u-ptm">
              <span v-text="worker.postal"></span>
            </div>
          </div>

          <div class="preview">
            <div class="preview-label">都道府県</div>
            <div class="preview-body u-ptm">
              <span v-text="prefectureName"></span>
            </div>
          </div>

          <div class="preview">
            <div class="preview-label">市区町村</div>
            <div class="preview-body u-ptm">
              <span v-text="worker.city_name"></span>
            </div>
          </div>

          <div class="preview">
            <div class="preview-label">転職意欲</div>
            <div class="preview-body u-ptm">
              <span v-text="jobChangeWillingnessName"></span>
            </div>
          </div>

          <div class="preview">
            <div class="preview-label">副業意欲</div>
            <div class="preview-body u-ptm">
              <span v-text="sideJobWillingnessName"></span>
            </div>
          </div>

          <div class="preview">
            <div class="preview-label">直近の職歴</div>
            <div class="preview-body u-ptm">
              <template v-for="occupation in worker.occupations">
                <WorkHistoryCard :occupation="occupation"></WorkHistoryCard>
              </template>
            </div>
          </div>

          <div class="preview">
            <div class="preview-label">パスワード設定</div>
            <div class="preview-body u-ptm">
              <div>********</div>
              <div class="u-pts">{{ worker.invitation_code }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="buttons">
        <div class="back-button" @click="$router.back()">
          <div>修正する</div>
        </div>
        <div class="submit-button" @click="submit">
          <div>この内容で作成</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="babel">
import Navbar from "@/components/navbar/Navbar";
import dayjs from 'dayjs';
import Signup from "@/api/v2/signup";
import {WORKER_KEY} from "@/constants/localStorageKeys";
import { E_PHONE_NUMBER_AUTHORIZATION_FAILED } from "@/constants/errorCodes";
import CheckCircleOutline from "vue-material-design-icons/CheckCircleOutline";
import HorizontalLine from "@/components/HorizontalLine";
import nativeMethodDispatcher from "@/utils/nativeMethodDispatcher";
import {computed, defineComponent, onMounted, inject} from "vue";
import {useStore} from "vuex/dist/vuex.esm-browser";
import {SIGNUP_STORE} from "@/constants/InjectionKeys";
import PREFS from "@/constants/prefs";
import {useRouter} from "vue-router/composables";
import {usePlatformType} from "@/composables/usePlatformType";
import {SIDE_JOB_WILLINGNESS} from "@/constants/sideJobWillingness";
import {JOB_CHANGE_WILLINGNESS} from "@/constants/jobChangeWillingness";
import WorkHistoryCard from "@/components/signup/WorkHistoryCard";
import {useAlert} from "@/composables/useAlert";
import {useGtag} from "@/composables/useGtag";
import Mypage from "@/api/v1/mypage";
import { captureMessage } from "@sentry/vue";

export default defineComponent({
  name: "Preview",
  components: { WorkHistoryCard, HorizontalLine, Navbar, CheckCircleOutline },
  setup(_, { emit }) {
    emit('page-navbar', false)

    const store = useStore()
    store.dispatch('signup/fetchNationalities')

    const { worker } = inject(SIGNUP_STORE)

    const formattedGender = computed(() => {
      switch (parseInt(worker.gender)) {
        case 1:
          return '男性';
        case 2:
          return '女性';
      }
    })
    const nationalityName = computed(() => {
      const nationalities = store.getters['signup/nationalities']
      if (nationalities.length > 0) {
        return nationalities.find((nationality) => nationality.code === (worker.nationality || 'JPN')).name;
      }
    })
    const prefectureName = computed(() => {
      const pref = PREFS.find((pref) => pref.id === worker.pref_id)
      if (pref) {
        return pref.name
      }
      captureMessage(`prefecture not found: ${worker.pref_id}`)
      return ''
    })
    const sideJobWillingnessName = computed(() => {
      const sideJobWillingness = SIDE_JOB_WILLINGNESS.find((willingness) => willingness.value === worker.side_job_willingness)
      return sideJobWillingness ? sideJobWillingness.name : 'なし'
    })
    const jobChangeWillingnessName = computed(() => {
      const jobChangeWillingness = JOB_CHANGE_WILLINGNESS.find((willingness) => willingness.value === worker.job_change_willingness)
      return jobChangeWillingness ? jobChangeWillingness.name : 'なし'
    })

    const formatDate = (date) => dayjs(date).format('YYYY年MM月DD日')

    const router = useRouter()
    const gtag = useGtag();
    const { showErrorAlert } = useAlert()

    const submit = () => {
      emit('loading', true)
      const { isNative } = usePlatformType()

      Signup.signup({ ...worker, registered_from: isNative() ? 'app' : 'web' }).then((response) => {
        const payload = response.data.payload;
        store.dispatch('auth/setToken', payload)

        if (isNative()) {
          const message = `token=${payload.access_token}&refresh_token=${payload.refresh_token}&expires_in=${payload.expires_in}`;
          nativeMethodDispatcher.dispatch('worker', message);
        } else {
          gtag.event('completedSignupProfile', { event_category: 'signup' });
        }

        localStorage.removeItem(WORKER_KEY);

        Mypage.get()
          .then((response) => {
            const payload = response.data.payload
            const isPassed = payload.recruit_status === 7

            router.push({ name: isPassed ? 'completed' : 'registrationCompleted' });
          })
          .catch(showErrorAlert)
          .finally(() => {
            emit('loading', false)
          })
      }).catch((response) => {
        emit('loading', false)
        showErrorAlert(response);

        if (response.data != null) {
          const errorCodes = response.data.errors.map((error) => error.code);

          // 電話認証エラーになったら電話認証まで戻す
          if (errorCodes.includes(E_PHONE_NUMBER_AUTHORIZATION_FAILED)) {
            router.push({ name: 'phoneNumberAuthorization' });
          }
        }
      })
    }

    return {
      worker,
      formattedGender,
      nationalityName,
      prefectureName,
      sideJobWillingnessName,
      jobChangeWillingnessName,
      formatDate,
      submit
    }
  }
})
</script>

<style scoped lang="scss">
.title {
  font-weight: bold;
  font-size: 22px;
  line-height: 18px;

  &-item {
    display: flex;
    align-items: center;
  }
}

.preview {
  padding-bottom: 40px;

  &-circle-icon {
    display: flex;
  }

  &-label {
    font-weight: bold;
    font-size: 16px;
    line-height: 16px;
  }

  &-body {
    font-size: 14px;
    line-height: 14px;
    color: #4E4B4B;
  }

  &-modify-button {
    font-weight: 500;
    font-size: 16px;
    line-height: 16px;
    letter-spacing: 0.03em;
    text-align: center;
    padding: 18px 0;

    border: 1px solid #E5E5E5;
    box-sizing: border-box;
    border-radius: 40px;
  }

  &-skill-item {
    padding: 6px 16px;
    display: flex;
    align-items: center;
    color: white;
    background: linear-gradient(90deg, #FF9880 0%, #F94848 100%);
    box-sizing: border-box;
    border-radius: 26px;

    span {
      font-size: 12px;
      line-height: 12px;
    }
  }
}

.buttons {
  display: flex;
  flex-direction: row;
  gap: 8px;

  .back-button {
    flex: 1;
    text-align: center;

    div {
      background: #FFFFFF;
      box-shadow: 0 5px 12px rgba(111, 0, 24, 0.2);
      border-radius: 100px;

      font-weight: bold;
      font-size: 16px;
      line-height: 14px;
      color: #1D1918;
      text-align: center;

      padding: 20px;
    }
  }

  .submit-button {
    flex: 1;
    text-align: center;

    div {
      background: #F94848;
      box-shadow: 0 5px 12px rgba(111, 0, 24, 0.2);
      border-radius: 100px;

      font-weight: bold;
      font-size: 16px;
      line-height: 14px;
      color: white;
      text-align: center;

      padding: 20px;
    }
  }
}
</style>
